import React, { useEffect } from "react";

import { Link } from "react-router-dom";
import moment from "moment-timezone";

import { useRoutes } from "utils/routes";

import { useCurrentOrganization } from "providers/WithCurrentOrganization";
import { Flash, useSetFlashes } from "providers/WithFlashes";
import pluralize from "pluralize";

const DAYS_BEFORE_EXPIRY_WARNING = 14;

const WarnIfSamlCertExpiring: React.FunctionComponent = () => {
  const { organizationIntegrations } = useRoutes();
  const currentOrganization = useCurrentOrganization(true);
  const setFlashes = useSetFlashes();

  const { slug, samlIntegration } = currentOrganization ?? {};
  const idpCertExpiresAt = samlIntegration?.idpCertExpiresAt;
  const integrationsPath = organizationIntegrations(slug);

  const now = moment();
  const expiresAt = idpCertExpiresAt ? moment.unix(idpCertExpiresAt) : null;
  const daysUntilExpiry = expiresAt ? expiresAt.diff(now, "days") : null;

  const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;
  const isExpiringSoon =
    daysUntilExpiry !== null &&
    daysUntilExpiry >= 0 &&
    daysUntilExpiry <= DAYS_BEFORE_EXPIRY_WARNING;

  const skipFlash = !slug || (!isExpired && !isExpiringSoon);

  useEffect(() => {
    const flash: Flash = {
      level: "alert",
      msg: (
        <span>
          {isExpired ? (
            <>
              Your organization's SAML certificate has expired. Single Sign-On
              will not work until the certificate is updated.
            </>
          ) : (
            <>
              Your organization's SAML certificate will expire in{" "}
              {pluralize("day", daysUntilExpiry, true)}.
            </>
          )}{" "}
          Please <Link to={integrationsPath}>update the certificate</Link>.
        </span>
      ),
      persist: true,
    };
    if (!skipFlash) {
      setFlashes((currFlashes) => currFlashes.concat(flash));
    }
    return () => {
      setFlashes((currFlashes) => currFlashes.filter((f) => f != flash));
    };
  }, [setFlashes, skipFlash, isExpired, daysUntilExpiry, integrationsPath]);
  return null;
};

export default WarnIfSamlCertExpiring;
